/**
 * IQulture Badge Assets
 *
 * Badge images for the IQulture scoring system.
 * PRO (>80), POTENTIAL (50-80), NOOB (≤50)
 */

import type { AssetPath, IQultureBadge } from '../types';

/**
 * Base path for badge assets
 */
const BADGES_BASE = '/assets/badges';

/**
 * IQulture badge assets with metadata (SVG versions - small)
 */
export const IQULTURE_BADGE_ASSETS: Record<IQultureBadge, AssetPath> = {
  pro: {
    path: `${BADGES_BASE}/iqulture/badge_pro.svg`,
    alt: 'PRO Badge - IQulture score above 80',
    width: 120,
    height: 120,
  },
  potential: {
    path: `${BADGES_BASE}/iqulture/badge_potential.svg`,
    alt: 'POTENTIAL Badge - IQulture score 50-80',
    width: 120,
    height: 120,
  },
  noob: {
    path: `${BADGES_BASE}/iqulture/badge_noob.svg`,
    alt: 'NOOB Badge - IQulture score below 50',
    width: 120,
    height: 120,
  },
  no_badge: {
    path: `${BADGES_BASE}/iqulture/badge_no_badge.svg`,
    alt: 'No Badge - Assessment not completed',
    width: 120,
    height: 120,
  },
} as const;

/**
 * IQulture badge full/detailed assets (AVIF versions - larger, more detailed)
 */
export const IQULTURE_BADGE_FULL_ASSETS: Partial<Record<IQultureBadge, AssetPath>> = {
  pro: {
    path: `${BADGES_BASE}/iqulture/badge_pro_full.avif`,
    alt: 'PRO Badge (Full) - IQulture score above 80',
    width: 512,
    height: 512,
  },
  potential: {
    path: `${BADGES_BASE}/iqulture/badge_potential_full.avif`,
    alt: 'POTENTIAL Badge (Full) - IQulture score 50-80',
    width: 512,
    height: 512,
  },
  noob: {
    path: `${BADGES_BASE}/iqulture/badge_noob_full.avif`,
    alt: 'NOOB Badge (Full) - IQulture score below 50',
    width: 512,
    height: 512,
  },
} as const;

/**
 * IQulture badge paths (simple string access)
 */
export const IQULTURE_BADGES: Record<IQultureBadge, string> = {
  pro: IQULTURE_BADGE_ASSETS.pro.path,
  potential: IQULTURE_BADGE_ASSETS.potential.path,
  noob: IQULTURE_BADGE_ASSETS.noob.path,
  no_badge: IQULTURE_BADGE_ASSETS.no_badge.path,
} as const;

/**
 * IQulture badge display names
 */
export const IQULTURE_BADGE_NAMES: Record<IQultureBadge, string> = {
  pro: 'PRO',
  potential: 'POTENTIAL',
  noob: 'NOOB',
  no_badge: 'Not Assessed',
} as const;

/**
 * IQulture badge descriptions
 */
export const IQULTURE_BADGE_DESCRIPTIONS: Record<IQultureBadge, string> = {
  pro: 'High readiness for international mobility',
  potential: 'Good foundation with room for growth',
  noob: 'Early stage in mobility preparation',
  no_badge: 'Complete CheckUp to get your badge',
} as const;

/**
 * Get IQulture badge path
 */
export function getIQultureBadge(badge: IQultureBadge): string {
  return IQULTURE_BADGES[badge];
}

/**
 * Get IQulture badge asset with metadata
 */
export function getIQultureBadgeAsset(badge: IQultureBadge): AssetPath {
  return IQULTURE_BADGE_ASSETS[badge];
}

/**
 * Get IQulture badge full asset (larger, more detailed version)
 */
export function getIQultureBadgeFullAsset(badge: IQultureBadge): AssetPath | undefined {
  return IQULTURE_BADGE_FULL_ASSETS[badge];
}

/**
 * Get IQulture badge name
 */
export function getIQultureBadgeName(badge: IQultureBadge): string {
  return IQULTURE_BADGE_NAMES[badge];
}
